import React from "react";
import ReactMarkdown from "react-markdown";
import { CDRSession } from "./types";
import UserRoleIcon from "src/components/UserRoleIcon";
import CollapsibleCard from "src/components/CollapsibleCard";
import { HttpMethod, callApi } from "src/services/apiService";
import styles from "./SessionCard.module.css";

interface SessionCardProps {
  session: CDRSession;
}

// Function to format duration by rounding to nearest tenth of a second
const formatDuration = (duration: string): string => {
  // Return as-is if not a time format or if it's a non-time string like "N/A", "Error", etc.
  if (
    !duration ||
    !duration.includes(":") ||
    duration === "N/A" ||
    duration.includes("Error")
  ) {
    return duration;
  }

  // Check if duration contains decimal seconds
  const timePattern = /^(\d{2}):(\d{2}):(\d{2})\.(\d+)$/;
  const match = duration.match(timePattern);

  if (match) {
    const hours = match[1];
    const minutes = match[2];
    const seconds = parseInt(match[3]);
    const fractionalPart = match[4];

    // Take only the first decimal place and round appropriately
    const firstDecimal = parseInt(fractionalPart.charAt(0) || "0");
    const secondDecimal = parseInt(fractionalPart.charAt(1) || "0");

    // Round to nearest tenth
    let roundedDecimal = firstDecimal;
    if (secondDecimal >= 5) {
      roundedDecimal = Math.min(9, firstDecimal + 1);
    }

    // Handle carry-over if rounding causes overflow
    if (roundedDecimal === 10) {
      const newSeconds = seconds + 1;
      if (newSeconds < 60) {
        return `${hours}:${minutes}:${newSeconds.toString().padStart(2, "0")}.0`;
      } else {
        // Would need to handle minute/hour overflow, but for simplicity, just return original
        return duration;
      }
    }

    return `${hours}:${minutes}:${seconds.toString().padStart(2, "0")}.${roundedDecimal}`;
  }

  // Return original duration if format doesn't match expected pattern
  return duration;
};

const SessionCard: React.FC<SessionCardProps> = ({ session }) => {
  // Helper function to get SIP code description
  const getSipCodeDescription = (code: string): string => {
    const descriptions: { [key: string]: string } = {
      "200": "OK - Call successful",
      "486": "Busy Here",
      "487": "Request Terminated",
      "404": "Not Found",
      "408": "Request Timeout",
      "480": "Temporarily Unavailable",
      "603": "Decline",
      "503": "Service Unavailable",
    };
    return descriptions[code] || "Unknown";
  };

  // Calculate percentages for call dispositions
  const calculatePercentage = (value: number, total: number): string => {
    if (total === 0) return "0%";
    return `${Math.round((value / total) * 100)}%`;
  };

  // Function to download violations file
  const downloadViolationsFile = async () => {
    try {
      const data = await callApi(
        `cdranalysis/download-violations/${session.sessionId}`,
        HttpMethod.GET,
      );

      if (!data?.downloadUrl || !data?.fileName) {
        throw new Error("Invalid response from download API");
      }

      // Create a temporary anchor element to trigger download
      // This is a well-established pattern for downloading files in the browser
      const link = document.createElement("a");
      link.href = data.downloadUrl;
      link.download = data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading violations file:", error);
      alert("Failed to download violations file. Please try again.");
    }
  };

  // Function to download source file
  const downloadSourceFile = async () => {
    try {
      const data = await callApi(
        `cdranalysis/download-source/${session.sessionId}`,
        HttpMethod.GET,
      );

      if (!data?.downloadUrl || !data?.fileName) {
        throw new Error("Invalid response from download API");
      }

      // Create a temporary anchor element to trigger download
      // This is a well-established pattern for downloading files in the browser
      const link = document.createElement("a");
      link.href = data.downloadUrl;
      link.download = data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading source file:", error);
      alert("Failed to download source file. Please try again.");
    }
  };

  // Reusable download source file button component
  const DownloadSourceButton = () => (
    <div className="mt-3">
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={downloadSourceFile}
      >
        <i className="bi bi-download me-2"></i>
        Download Original CDR File
      </button>
    </div>
  );

  // Use actual data
  const sipCodeData = session.sipCodeDistribution || {};

  // Use actual reassigned number data
  const reassignedNumberCalls = session.reassignedNumberCalls || 0;

  // Use actual calls per day per ANI data
  const callsPerDayPerANI = session.callsPerDayPerANI || {
    dates: [],
    data: [],
  };

  return (
    <div className="card mb-3">
      <div className="card-header bg-light">
        <div className="d-flex flex-wrap flex-md-nowrap align-items-center justify-content-between">
          <div className="fw-bold">
            <i className={`bi bi-filetype-csv me-2`}></i>
            {session.fileName}
          </div>

          <div className="d-flex align-items-center">
            <i className="bi bi-calendar2 me-2"></i> {session.uploadDate}
          </div>

          <div className="d-flex align-items-center">
            <span className="fw-bold me-1">Uploaded By:</span>
            {session.bitmap !== undefined && (
              <UserRoleIcon Role={session.UploadedByRoleId} />
            )}
            {session.UploadedByFullName}
          </div>
        </div>
      </div>
      <div className="card-body">
        {session.isProcessing && !session.errorMessage ? (
          <>
            <div className="alert alert-info">
              <div className="d-flex align-items-center">
                {/* Hiding spinner for now */}
                <div
                  className="spinner-border spinner-border-sm me-2 d-none"
                  role="status"
                >
                  <span className="visually-hidden">Processing...</span>
                </div>
                <div>
                  <h5 className="mb-1 text-black">Processing CDR Analysis</h5>
                  <p className="mb-0">
                    Your CDR file is currently being analyzed. This may take a
                    few minutes depending on file size. You will have to refresh
                    the page to see results.
                  </p>
                </div>
              </div>
            </div>
            <DownloadSourceButton />
          </>
        ) : session.errorMessage ? (
          <>
            <div className="alert alert-danger">
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div>
                  <h5 className="mb-1 text-black">Analysis Error</h5>
                  <p className="mb-0">{session.errorMessage}</p>
                </div>
              </div>
            </div>
            <DownloadSourceButton />
          </>
        ) : (
          <>
            {/* Show invalid phone number warning if any were found */}
            {session.InvalidPhoneNumberCount > 0 && (
              <div className="alert alert-warning mb-3">
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div>
                    <strong>Data Quality Notice:</strong>{" "}
                    {session.InvalidPhoneNumberCount.toLocaleString()} lines
                    with invalid phone numbers (not 10-11 digits) were
                    discarded.
                  </div>
                </div>
              </div>
            )}

            <div className={styles.statsContainer}>
              <div className={styles.statCard}>
                <div className={styles.statCardBody}>
                  <h5 className={styles.statCardTitle}>Total Calls</h5>
                  <p className={styles.statCardValue}>
                    {session.totalCalls?.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statCardBody}>
                  <h5 className={styles.statCardTitle}>Connected Calls</h5>
                  <p className={styles.statCardValue}>
                    {session.connectedCalls.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statCardBody}>
                  <h5 className={styles.statCardTitle}>Avg. Duration</h5>
                  <p className={styles.statCardValue}>
                    {formatDuration(session.averageDuration)}
                  </p>
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statCardBody}>
                  <h5 className={styles.statCardTitle}>Answer Rate</h5>
                  <p className={styles.statCardValue}>{session.anserRate}</p>
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statCardBody}>
                  <h5 className={styles.statCardTitle}>
                    Avg Daily Calls Per ANI
                  </h5>
                  <p className={styles.statCardValue}>
                    {session.avgDailyCallsPerANI}
                  </p>
                </div>
              </div>
            </div>

            {/* AI Analysis Section */}
            {session.aiAnalysis && (
              <div className="row mb-4">
                <div className="col-12">
                  <CollapsibleCard
                    title="AI Analysis - Discover Key Patterns & Insights - Click to Expand"
                    icon="bi-lightbulb"
                    defaultExpanded={false}
                  >
                    <div className="alert alert-info">
                      <div className="d-flex align-items-start flex-grow-1 markdown-content">
                        <div className="markdown-content">
                          <ReactMarkdown>{session.aiAnalysis}</ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  </CollapsibleCard>
                </div>
              </div>
            )}

            <h5 className="mb-3">Compliance Analysis</h5>

            <div className="mb-3">
              <div className="col-12">
                <CollapsibleCard
                  title="Attempt Violations"
                  icon="bi-clock-history"
                >
                  <div className="d-flex flex-wrap gap-3">
                    {session.durationViolations.map((violation, index) => (
                      <div key={index} className="flex-grow-1 col-md-3 mb-3">
                        <div
                          className={`alert ${violation.violations > 0 ? "alert-warning" : "alert-success"}`}
                        >
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                              <h6 className="alert-heading">
                                {violation.displayName}
                              </h6>
                              <p className="mb-0 small">
                                {violation.description}
                              </p>
                            </div>
                            <div className="fs-4 fw-bold">
                              {violation.violations.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Download violations file button */}
                  {session.violationsBlobFile && (
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={downloadViolationsFile}
                      >
                        <i className="bi bi-download me-2"></i>
                        Download Detailed Violations Report
                      </button>
                    </div>
                  )}
                </CollapsibleCard>
              </div>
            </div>

            {/* Calling Window Violations will be added in Phase 2
            <div className="row mb-3">
              <div className="col-12">
                <CollapsibleCard
                  title="Calling Window Violations"
                  icon="bi-calendar-x"
                >
                  <div className="alert alert-warning">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="alert-heading">Outside Permitted Hours</h6>
                        <p className="mb-0 small">
                          Calls made outside of legal calling windows
                        </p>
                      </div>
                      <div className="fs-4 fw-bold">
                        {session.callingWindowViolations}
                      </div>
                    </div>
                  </div>
                </CollapsibleCard>
              </div>
            </div>
          */}

            <div className="row mb-3">
              <div className="col-12">
                <CollapsibleCard
                  title="Reassigned Numbers"
                  icon="bi-telephone-x"
                >
                  <div
                    className={`alert ${reassignedNumberCalls > 0 ? "alert-warning" : "alert-success"}`}
                  >
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="alert-heading">
                          Recently Reassigned Numbers
                        </h6>
                        <p className="mb-0 small">
                          Calls to numbers reassigned in the last 45 days
                        </p>
                      </div>
                      <div className="fs-4 fw-bold">
                        {reassignedNumberCalls.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </CollapsibleCard>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <CollapsibleCard
                  title="Calls Per Day Per ANI - Click to Expand"
                  icon="bi-telephone-outbound"
                  defaultExpanded={false}
                >
                  {() => (
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Phone</th>
                            {callsPerDayPerANI.dates.map(
                              (date: string, index: number) => (
                                <th key={index} className="text-center">
                                  {date}
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {callsPerDayPerANI.data.map(
                            (row: any, rowIndex: number) => (
                              <tr key={rowIndex}>
                                <td className="fw-bold">
                                  {row.ani.replace(
                                    /(\d{3})(\d{3})(\d{4})/,
                                    "($1) $2-$3",
                                  )}
                                </td>
                                {row.callCounts.map(
                                  (count: number, colIndex: number) => (
                                    <td key={colIndex} className="text-center">
                                      {count > 0 ? (
                                        <span
                                          className={
                                            count > 50
                                              ? "text-danger fw-bold"
                                              : ""
                                          }
                                        >
                                          {count}
                                        </span>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                  ),
                                )}
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CollapsibleCard>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <CollapsibleCard
                  title="SIP Response Code Distribution"
                  icon="bi-code-slash"
                >
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>SIP Code</th>
                          <th>Description</th>
                          <th className="text-center">Count</th>
                          <th className="text-center">Percentage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Calculate total count for percentages
                          let totalCount = 0;
                          for (const code in sipCodeData) {
                            totalCount += sipCodeData[code] as number;
                          }

                          return Object.entries(sipCodeData)
                            .sort(([codeA], [codeB]) => {
                              // Sort by SIP code numerically
                              return parseInt(codeA) - parseInt(codeB);
                            })
                            .map(([code, count]) => (
                              <tr key={code}>
                                <td
                                  className="fw-bold"
                                  style={{ width: "15%" }}
                                >
                                  <span className="badge bg-primary me-1">
                                    SIP
                                  </span>{" "}
                                  {code}
                                </td>
                                <td>{getSipCodeDescription(code)}</td>
                                <td
                                  className="text-center fw-bold"
                                  style={{ width: "15%" }}
                                >
                                  {count}
                                </td>
                                <td
                                  className="text-center"
                                  style={{ width: "15%" }}
                                >
                                  {calculatePercentage(
                                    count as number,
                                    totalCount,
                                  )}
                                </td>
                              </tr>
                            ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </CollapsibleCard>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <CollapsibleCard
                  title="Call Dispositions"
                  icon="bi-telephone-fill"
                >
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Disposition Type</th>
                          <th className="text-center">Count</th>
                          <th className="text-center">Percentage</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <span className="badge bg-success me-2">●</span>
                            Connected
                          </td>
                          <td className="text-center fw-bold">
                            {session.callDispositions?.connected || 0}
                          </td>
                          <td className="text-center">
                            {calculatePercentage(
                              session.callDispositions?.connected || 0,
                              session.callDispositions?.total || 0,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <span className="badge bg-secondary me-2">●</span>
                            No Answer
                          </td>
                          <td className="text-center fw-bold">
                            {session.callDispositions?.noAnswer || 0}
                          </td>
                          <td className="text-center">
                            {calculatePercentage(
                              session.callDispositions?.noAnswer || 0,
                              session.callDispositions?.total || 0,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <span className="badge bg-info me-2">●</span>
                            Voicemail
                          </td>
                          <td className="text-center fw-bold">
                            {session.callDispositions?.voicemail || 0}
                          </td>
                          <td className="text-center">
                            {calculatePercentage(
                              session.callDispositions?.voicemail || 0,
                              session.callDispositions?.total || 0,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <span className="badge bg-warning me-2">●</span>
                            Wrong Number
                          </td>
                          <td className="text-center fw-bold">
                            {session.callDispositions?.wrongNumber || 0}
                          </td>
                          <td className="text-center">
                            {calculatePercentage(
                              session.callDispositions?.wrongNumber || 0,
                              session.callDispositions?.total || 0,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <span className="badge bg-light text-dark me-2">
                              ●
                            </span>
                            Other
                          </td>
                          <td className="text-center fw-bold">
                            {session.callDispositions?.other || 0}
                          </td>
                          <td className="text-center">
                            {calculatePercentage(
                              session.callDispositions?.other || 0,
                              session.callDispositions?.total || 0,
                            )}
                          </td>
                        </tr>
                        <tr className="table-light">
                          <td className="fw-bold">Total</td>
                          <td className="text-center fw-bold">
                            {session.callDispositions?.total || 0}
                          </td>
                          <td className="text-center">100%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CollapsibleCard>
              </div>
            </div>

            {/* Download source file button at bottom of card */}
            <DownloadSourceButton />
          </>
        )}
      </div>
    </div>
  );
};

export default SessionCard;
